/* istanbul ignore next */

import Vue from 'vue';

const isServer = Vue.prototype.$isServer;
const SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g;
const MOZ_HACK_REGEXP = /^moz([A-Z])/;
// @ts-ignore
const ieVersion = isServer ? 0 : Number(document.documentMode);

/* istanbul ignore next */
const trim = (str: string) => {
    return (str || '').replace(/^[\s\uFEFF]+|[\s\uFEFF]+$/g, '');
};
/* istanbul ignore next */
const camelCase = (name: string) => {
    return name.replace(SPECIAL_CHARS_REGEXP, (_, separator, letter, offset) => {
        return offset ? letter.toUpperCase() : letter;
    }).replace(MOZ_HACK_REGEXP, 'Moz$1');
};

/* istanbul ignore next */
export const on = (() => {
    if (!isServer && document.addEventListener) {
        return (element: HTMLElement, event: string, handler: EventListenerOrEventListenerObject) => {
            if (element && event && handler) {
                element.addEventListener(event, handler, false);
            }
        };
    } else {
        return (element: HTMLElement, event: string, handler: EventListenerOrEventListenerObject) => {
            if (element && event && handler) {
                // @ts-ignore
                element.attachEvent('on' + event, handler);
            }
        };
    }
})();

/* istanbul ignore next */
export const off = (() => {
    if (!isServer && document.removeEventListener) {
        return (element: HTMLElement, event: string, handler: EventListenerOrEventListenerObject) => {
            if (element && event) {
                element.removeEventListener(event, handler, false);
            }
        };
    } else {
        return (element: HTMLElement, event: string, handler: EventListenerOrEventListenerObject) => {
            if (element && event) {
                // @ts-ignore
                element.detachEvent('on' + event, handler);
            }
        };
    }
})();

/* istanbul ignore next */
export const once = (el: HTMLEmbedElement, event: string, fn: EventListener) => {
    const listener = function() {
        if (fn) {
            // @ts-ignore
            fn.apply(this, arguments);
        }
        off(el, event, listener);
    };
    on(el, event, listener);
};

/* istanbul ignore next */
export function hasClass(el: HTMLElement, cls: string) {
    if (!el || !cls) {
        return false;
    }
    if (cls.indexOf(' ') !== -1) {
        throw new Error('className should not contain space.');
    }
    if (el.classList) {
        return el.classList.contains(cls);
    } else {
        return (' ' + el.className + ' ').indexOf(' ' + cls + ' ') > -1;
    }
}

/* istanbul ignore next */
export function addClass(el: HTMLElement, cls: string) {
    if (!el) {
        return;
    }
    let curClass = el.className;
    const classes = (cls || '').split(' ');

    for (let i = 0, j = classes.length; i < j; i++) {
        const clsName = classes[i];
        if (!clsName) {
            continue;
        }

        if (el.classList) {
            el.classList.add(clsName);
        } else {
            if (!hasClass(el, clsName)) {
                curClass += ' ' + clsName;
            }
        }
    }
    if (!el.classList) {
        el.className = curClass;
    }
}

/* istanbul ignore next */
export function removeClass(el: HTMLElement, cls: string) {
    if (!el || !cls) {
        return;
    }
    const classes = cls.split(' ');
    let curClass = ' ' + el.className + ' ';

    for (let i = 0, j = classes.length; i < j; i++) {
        const clsName = classes[i];
        if (!clsName) {
            continue;
        }

        if (el.classList) {
            el.classList.remove(clsName);
        } else {
            if (hasClass(el, clsName)) {
                curClass = curClass.replace(' ' + clsName + ' ', ' ');
            }
        }
    }
    if (!el.classList) {
        el.className = trim(curClass);
    }
}

/* istanbul ignore next */
export const getStyle = ieVersion < 9 ? (element: HTMLElement, styleName: string) => {
    if (isServer) {
        return;
    }
    if (!element || !styleName) {
        return null;
    }
    styleName = camelCase(styleName);
    if (styleName === 'float') {
        styleName = 'styleFloat';
    }
    try {
        switch (styleName) {
        case 'opacity':
            try {
                // @ts-ignore
                return element.filters.item('alpha').opacity / 100;
            } catch (e) {
                return 1.0;
            }
        default:
        // @ts-ignore
            return (element.style[styleName] || element.currentStyle ? element.currentStyle[styleName] : null);
        }
    } catch (e) {
        // @ts-ignore
        return element.style[styleName];
    }
} : (element: HTMLElement, styleName: string) => {
    if (isServer) {
        return;
    }
    if (!element || !styleName) {
        return null;
    }
    styleName = camelCase(styleName);
    if (styleName === 'float') {
        styleName = 'cssFloat';
    }
    try {
        // @ts-ignore
        const computed = document.defaultView.getComputedStyle(element, '');
        // @ts-ignore
        return element.style[styleName] || computed ? computed[styleName] : null;
    } catch (e) {
        // @ts-ignore
        return element.style[styleName];
    }
};

/* istanbul ignore next */
export function setStyle(element: HTMLElement, styleName: object | string, value: any) {
    if (!element || !styleName) {
        return;
    }

    if (typeof styleName === 'object') {
        for (const prop in styleName) {
            if (styleName.hasOwnProperty(prop)) {
                // @ts-ignore
                setStyle(element, prop, styleName[prop]);
            }
        }
    } else {
        styleName = camelCase(styleName);
        if (styleName === 'opacity' && ieVersion < 9) {
            element.style.filter = isNaN(value) ? '' : 'alpha(opacity=' + value * 100 + ')';
        } else {
            // @ts-ignore
            element.style[styleName] = value;
        }
    }
}
